import * as React from 'react';

import type { CampaignAuditEntryDto } from '@/api/rank-admin-client';
import { useCampaignAudit } from '@/api/use-campaign-audit';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

// =============================================================================
// campaign-audit-screen.tsx — Écran ADMIN « Audit » de la zone d'analyse campagne (LANE-E-CAMPAIGN-AUDIT).
// =============================================================================
// A0R-9 : liste les actions auditées d'une campagne (transitions d'état, exports, publications, figements
// RANG), DÉJÀ émises et projetées dans audit_entry (schéma audit GLOBAL). Read-model ADMIN read-only via
// l'API (GET /admin/campaigns/:campaignId/audit) — l'UI LIT et habille, AUCUNE règle métier (autorité API :
// RBAC AdminSessionGuard, lecture audit, tri occurredAt DESC, flag irréversible A0R-11).
//
// A0R-11 : les actions IRRÉVERSIBLES (publication Pulse, abandon, soumission finale, clôture, nouvelle
// version CGU) sont signalées « comme telles » par un badge TEXTE « Irréversible » — le flag vient de l'API
// (dérivé à la lecture) ; le front ne le recalcule pas.
//
// SCOPE ADMIN = pas de cloisonnement tenant applicatif (audit GLOBAL) ; doctrine NON-NOMINATIF respectée en
// amont (le read-model n'expose ni payload brut ni tenant_id). L'écran n'affiche donc aucune PII tierce.
//
// WCAG 2.1 AA (ST-NFR-03) :
//   - <table> avec <caption> + <th scope="col"/"row"> ; <time dateTime> pour l'horodatage ;
//   - le badge « Irréversible » porte un LIBELLÉ TEXTE (la couleur n'est qu'un renfort, jamais seule) ;
//   - états de chargement/erreur/vide en role="status" / role="alert".

// D9 (Gate Human 2026-09-15) : HOMOGÉNÉITÉ du niveau de détail. La colonne « Action » doit TOUJOURS
// afficher un libellé HUMAIN — jamais un code brut `DE.*` (« DE.CampaignCycleClosed n'est pas parlant pour
// un utilisateur lambda »). Le read-model d'audit campagne (BC analytics, CampaignAuditReadService) est un
// PASS-THROUGH du `eventType` d'audit_entry : n'importe quel Domain Event campaign-scoped peut apparaître —
// cycle de vie campagne, soumissions (rate-collection), fils de discussion (communication), benchmark/panel
// (analytics), CGU (legal), catalogue. La table ci-dessous couvre ces familles ; tout code inconnu dégrade
// via humanizeEventCode (jamais de `DE.*` visible).
const EVENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  // — Cycle de vie de la campagne (BC campaign) —
  'DE.CampaignOpened': 'Ouverture de la campagne',
  'DE.CampaignCycleClosed': 'Clôture d’un cycle',
  'DE.CampaignClosed': 'Clôture définitive de la campagne',
  'DE.CampaignAbandoned': 'Abandon de la campagne',
  'DE.CampaignParticipantAdded': 'Ajout d’un participant à la campagne',
  'DE.NegotiationOpened': 'Ouverture d’une négociation',
  // — Soumissions fournisseur (BC rate-collection) —
  'DE.SubmissionSubmitted': 'Soumission transmise',
  'DE.SubmissionLocked': 'Soumission finale verrouillée',
  'DE.SubmissionCloned': 'Soumission dupliquée',
  // — Benchmark / panel / analyse (BC analytics) —
  'DE.PanelFrozen': 'Figement du panel',
  'DE.BenchmarkExported': 'Export du benchmark',
  'DE.AnalysisPublishedToPulse': 'Publication du benchmark (Pulse)',
  'DE.AnalysisUnpublished': 'Dépublication du benchmark',
  'DE.CorrelationEdited': 'Modification d’une corrélation de profil',
  'DE.SupplierRankingAssigned': 'Attribution d’un rang fournisseur',
  'DE.SupplierRankingRemoved': 'Retrait d’un rang fournisseur',
  // — Fils de discussion et communication (BC communication) —
  'DE.ChatThreadProvisioned': 'Fil de discussion créé',
  'DE.ChatThreadArchived': 'Fil de discussion archivé',
  'DE.MessageSent': 'Message envoyé',
  'DE.MessageReadByCounterpart': 'Message lu par le destinataire',
  'DE.AnnouncementPublished': 'Annonce publiée',
  'DE.AnnouncementCancelled': 'Annonce annulée',
  'DE.ReminderSent': 'Relance envoyée',
  // — CGU (BC legal, contexte campagne) —
  'DE.CguVersionDrafted': 'Version CGU rédigée',
  'DE.CguVersionPublished': 'Nouvelle version CGU publiée',
  'DE.CguAcceptanceRecorded': 'Acceptation des CGU enregistrée',
  'DE.CguRelanceRequested': 'Relance CGU demandée',
  'DE.CguResignatureRequired': 'Nouvelle signature CGU requise',
  'DE.CguResignatureForced': 'Nouvelle signature CGU forcée',
  // — Catalogue (BC catalog, version rattachée à la campagne) —
  'DE.CatalogVersionDrafted': 'Version de catalogue rédigée',
  'DE.CatalogVersionPublished': 'Version de catalogue publiée',
  'DE.CatalogVersionArchived': 'Version de catalogue archivée',
  'DE.CatalogUploadAttemptCompleted': 'Import de catalogue traité',
};

/**
 * Repli D9 : un code d'événement INCONNU (non listé ci-dessus) ne doit JAMAIS s'afficher tel quel
 * (`DE.xxx`). On retire le préfixe `DE.` et on présente le nom en clair (découpage des mots collés en
 * CamelCase). Dégradation propre plutôt que fuite d'un code technique. Repli du repli = type brut si vide.
 */
function humanizeEventCode(eventType: string): string {
  const spaced = eventType
    .replace(/^DE\./, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
  if (spaced.length === 0) {
    return eventType;
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Type d'événement → libellé HUMAIN (D9). Repli robuste = code humanisé, jamais le code brut `DE.*`. */
export function eventTypeLabel(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType] ?? humanizeEventCode(eventType);
}

/**
 * Horodatage d'audit (D10) : DATE + HEURE précises, pas la date seule. Fuseau de L'UTILISATEUR
 * (cohérence avec les autres formateurs de la zone campagne, correctif 2026-09-15 : PAS de
 * `timeZone:'UTC'`) ; déterminisme des tests figé par `TZ` (vitest.config). Repli = ISO brut.
 */
function formatOccurredAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

export interface CampaignAuditScreenProps {
  readonly campaignId: string;
}

export function CampaignAuditScreen({
  campaignId,
}: CampaignAuditScreenProps): React.JSX.Element {
  const auditQuery = useCampaignAudit(campaignId);
  const entries = auditQuery.data?.entries ?? [];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Audit de la campagne
          </h1>
          <p className="text-sm text-muted-foreground">
            Journal des actions auditées, de la plus récente à la plus ancienne.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Actions auditées</CardTitle>
          <CardDescription>
            Transitions d’état, exports, publications et figements du panel. Les
            actions irréversibles sont signalées comme telles.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {auditQuery.isPending ? (
            <p role="status" className="text-sm text-muted-foreground">
              Chargement du journal d’audit…
            </p>
          ) : auditQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">
              Erreur de chargement du journal d’audit : {auditQuery.error.message}
            </p>
          ) : entries.length === 0 ? (
            <p role="status" className="text-sm text-muted-foreground">
              Aucune action auditée pour cette campagne pour le moment.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="ds-table w-full border-collapse text-sm">
                <caption className="sr-only">
                  Journal des actions auditées de la campagne, du plus récent au
                  plus ancien. Les actions irréversibles portent la mention
                  « Irréversible ».
                </caption>
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th scope="col" className="px-3 py-2 font-medium">
                      Action
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Date et heure
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Auteur
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Nature
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, index) => (
                    <AuditRow
                      key={`${entry.eventType}:${entry.occurredAt}:${String(index)}`}
                      entry={entry}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Une ligne du journal d'audit : action + horodatage + auteur + nature (badge « Irréversible » A0R-11). */
function AuditRow({
  entry,
}: {
  readonly entry: CampaignAuditEntryDto;
}): React.JSX.Element {
  return (
    <tr className="border-b border-border last:border-0">
      <th scope="row" className="px-3 py-2 text-left align-top font-medium">
        {eventTypeLabel(entry.eventType)}
      </th>
      <td className="px-3 py-2 align-top tabular-nums">
        <time dateTime={entry.occurredAt}>{formatOccurredAt(entry.occurredAt)}</time>
      </td>
      <td className="px-3 py-2 align-top">{entry.actorRef}</td>
      <td className="px-3 py-2 align-top">
        {entry.irreversible ? (
          <Badge variant="accent">Irréversible</Badge>
        ) : (
          <span className="text-muted-foreground">Réversible</span>
        )}
      </td>
    </tr>
  );
}
